/**
 * The doc routes (slice 2d spec §11) and their query keys. `['docs']` is the
 * list (every tab and search under it), `['doc', id]` one doc with its body;
 * the `doc` hub event invalidates both, and its tasks and threads, and a
 * `task`, `turn` or `thread.message` event refreshes every doc page's links
 * (`app/events.ts`, §11.6).
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { api, ApiError } from "../../app/api";
import { meKey } from "../../app/me";
import type { AreaRef } from "../areas/api";
import { taskKey, type TaskStage } from "../tasks/api";

/** Who wrote or archived a doc: a person or an agent, "Someone" once they're gone. */
export type DocWho = {
  kind: "person" | "agent";
  id: string | null;
  name: string;
  initials?: string;
  glyph?: string;
  tint?: string;
};

export type DocLinkTarget = "task" | "thread" | "routine";
/** How a thread came to the doc: "made this doc", "edited this doc", "asked about this doc". */
export type DocLinkVia = "made" | "edited" | "attached" | "linked";

export type DocLink = {
  target: DocLinkTarget;
  id: string;
  title: string;
  /** A task's. */
  stage?: TaskStage | null;
  for?: { id: string; name: string; initials: string; tint: string } | null;
  /** The target's last change: a task's last event, a thread's last message. */
  updated_at: string | null;
  linked_at: string;
  via: DocLinkVia;
};

/** `[start, end)` ranges where a search matched (§19 note 33). */
export type Marks = [number, number][];

/** A file doc's file (slice 5b spec §9): what it is, and whether Crew has read its text. */
export type DocFile = {
  id: string;
  name: string;
  mime: string;
  size: number;
  kind: "photo" | "file";
  text_status: "pending" | "ready" | "failed" | "none";
};

/** A doc as the list, a thread and Task detail show it (§11.1): no body. */
export type DocItem = {
  id: string;
  title: string;
  /** `file`: a file doc (slice 5b), its text its file's. */
  format: "markdown" | "file";
  /** A file doc's file; null (or missing, an older Worker) for a markdown doc. */
  file?: DocFile | null;
  version: number;
  length: number;
  snippet: string;
  marks: { title: Marks; snippet: Marks };
  /** Tasks first, then the newest change. */
  links: DocLink[];
  updated_by: DocWho;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
  /** Its Area's chip (slice 6d spec §4.4), null with none. Missing on an older Worker. */
  area?: AreaRef | null;
  area_id?: string | null;
};

/** The doc page's doc (§11.2). */
export type Doc = DocItem & {
  body: string;
  created_by: DocWho;
  archived_by: DocWho | null;
};

export type DocsTab = "all" | "archived";
export type DocCounts = { all: number; archived: number };
type DocsPage = { items: DocItem[]; counts: DocCounts; next_cursor: string | null };

/**
 * The copy to keep: a fetch or a save's reply, unless the cache holds a
 * newer save. Not Memory's `fresher`: archiving and links change a doc
 * without a save, so the same version can come back changed.
 */
export function newer(cached: Doc | undefined, next: Doc): Doc {
  return cached && cached.version > next.version ? cached : next;
}

export const docsKey = ["docs"] as const;
export const docKey = (id: string) => ["doc", id] as const;

function docsPath(tab: DocsTab, q: string, cursor: string | null, area: string | null): string {
  const params = new URLSearchParams();
  if (q.trim()) params.set("q", q.trim());
  if (area) params.set("area", area);
  if (tab === "archived") params.set("archived", "1");
  if (cursor) params.set("cursor", cursor);
  const query = params.toString();
  return query ? `/api/docs?${query}` : "/api/docs";
}

/** One tab of Docs, or a search in it, filtered to an Area's slug or `none` (slice 6d); 30 a page. */
export function useDocs(tab: DocsTab, q: string, area: string | null = null) {
  return useInfiniteQuery({
    queryKey: area ? [...docsKey, tab, q.trim(), area] : [...docsKey, tab, q.trim()],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => api<DocsPage>("GET", docsPath(tab, q, pageParam, area)),
    getNextPageParam: (page) => page.next_cursor,
  });
}

/**
 * One doc; a fetch never replaces a newer cached save (`newer`). `enabled`
 * false reads only what's cached (a thread's card while its `docs` list it).
 */
export function useDoc(id: string, enabled = true) {
  return useQuery({
    queryKey: docKey(id),
    enabled,
    queryFn: async () => (await api<{ doc: Doc }>("GET", `/api/docs/${id}`)).doc,
    structuralSharing: (old: unknown, next: unknown): Doc =>
      newer(old as Doc | undefined, next as Doc),
    // Hub events keep it fresh; a refetch while editing only raises "Reload?".
    staleTime: 5_000,
  });
}

/** Puts a copy of the doc into the cache (unless it holds a newer one) and refreshes the list. */
function storeDoc(client: QueryClient, doc: Doc) {
  client.setQueryData<Doc>(docKey(doc.id), (old) => newer(old, doc));
  void client.invalidateQueries({ queryKey: docsKey });
}

/** Refreshes the tasks a doc is linked to: Task detail lists its docs. */
function refreshTasks(client: QueryClient, doc: Pick<DocItem, "links">) {
  for (const link of doc.links) {
    if (link.target === "task") void client.invalidateQueries({ queryKey: taskKey(link.id) });
  }
}

type NewDoc = { title: string; body: string; task_id?: string; area_id?: string };

/** New doc's first Save (§10.5): the doc is made here, linked to the task. */
export function useCreateDoc() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: NewDoc) => (await api<{ doc: Doc }>("POST", "/api/docs", input)).doc,
    onSuccess: (doc) => {
      storeDoc(client, doc);
      refreshTasks(client, doc);
    },
  });
}

/**
 * A file doc of a kept file (slice 5b spec §6.1, §6.3): an upload in Docs or
 * Task detail, or Save to Docs on a message's file. Linked to the task, or
 * the thread it was in.
 */
export function useCreateFileDoc() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      file_id: string;
      task_id?: string;
      area_id?: string;
      thread_id?: string;
    }) => (await api<{ doc: Doc }>("POST", "/api/docs", input)).doc,
    onSuccess: (doc) => {
      storeDoc(client, doc);
      refreshTasks(client, doc);
      // A thread's chips learn they're docs now.
      void client.invalidateQueries({ queryKey: ["thread"] });
    },
  });
}

/** Delete (slice 5b spec §8): an archived doc, for good. The lists and its tasks refresh. */
export function useDeleteDoc(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api("DELETE", `/api/docs/${id}`),
    onSuccess: () => {
      const doc = client.getQueryData<Doc>(docKey(id));
      if (doc) refreshTasks(client, doc);
      client.removeQueries({ queryKey: docKey(id) });
      void client.invalidateQueries({ queryKey: docsKey });
      void client.invalidateQueries({ queryKey: ["thread"] });
    },
  });
}

/** A refused save (§6.4): `409 conflict` carries the current doc. */
export type SaveRefusal =
  | { kind: "conflict"; doc: Doc; hint: string }
  | { kind: "other"; hint: string };

export function refusalOf(error: unknown): SaveRefusal | null {
  if (!(error instanceof ApiError)) return null;
  const doc = error.body.doc as Doc | undefined;
  if (error.code === "conflict" && typeof doc?.id === "string" && Number.isInteger(doc.version)) {
    return { kind: "conflict", doc, hint: error.hint };
  }
  return { kind: "other", hint: error.hint };
}

/** A save: a title, a body, or (Replace, slice 5b) a file doc's new file, from `version`. */
type DocSave = { title?: string; body?: string; file_id?: string; version: number };

/** A person's save from `version` (§6.1); a stale one is `409 conflict` with the current doc. */
export function useSaveDoc(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (save: DocSave) =>
      (await api<{ doc: Doc }>("PUT", `/api/docs/${id}`, save)).doc,
    onSuccess: (doc) => storeDoc(client, doc),
    // The conflict's doc is the doc now: Use theirs, and a later save, start from it.
    onError: (error) => {
      const refused = refusalOf(error);
      if (refused?.kind === "conflict") storeDoc(client, refused.doc);
    },
  });
}

/** Archive or Restore (§11.2): not a save, so the page refetches for `archived_at`. */
export function useArchiveDoc(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (archive: boolean) =>
      api("POST", `/api/docs/${id}/${archive ? "archive" : "restore"}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: docKey(id) });
      void client.invalidateQueries({ queryKey: docsKey });
      const doc = client.getQueryData<Doc>(docKey(id));
      if (doc) refreshTasks(client, doc);
    },
  });
}

/**
 * Its Area (slice 6d spec §4.2): not a save, so `version` stays. The doc, the
 * list, every Area page and the sidebar refresh.
 */
export function useMoveDoc(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (areaId: string | null) =>
      (await api<{ doc: Doc }>("PATCH", `/api/docs/${id}`, { area_id: areaId })).doc,
    onSuccess: (doc) => {
      client.setQueryData<Doc>(docKey(id), (old) => (old ? { ...old, ...doc } : old));
      void client.invalidateQueries({ queryKey: docKey(id) });
      void client.invalidateQueries({ queryKey: docsKey });
      void client.invalidateQueries({ queryKey: ["area"] });
      void client.invalidateQueries({ queryKey: meKey });
    },
  });
}

/** Removes one of the doc's links (§10.3's ×). */
export function useUnlinkDoc(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (link: Pick<DocLink, "target" | "id">) =>
      api("DELETE", `/api/docs/${id}/links/${link.target}/${link.id}`),
    onSuccess: (_done, link) => {
      // Gone from the page at once; the refetch confirms it.
      client.setQueryData<Doc>(docKey(id), (old) =>
        old
          ? {
              ...old,
              links: old.links.filter((l) => !(l.target === link.target && l.id === link.id)),
            }
          : old,
      );
      void client.invalidateQueries({ queryKey: docKey(id) });
      void client.invalidateQueries({ queryKey: docsKey });
      if (link.target === "task") void client.invalidateQueries({ queryKey: taskKey(link.id) });
      if (link.target === "thread")
        void client.invalidateQueries({ queryKey: ["thread", link.id] });
    },
  });
}
